import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { NudgeState } from "@starsach/shared-types";
import { useAuth } from "../auth/AuthProvider";
import { passkeysPossible } from "../auth/cognito-api";
import { usePasskeyCount } from "../auth/usePasskeys";
import { laterAt, visibleNudge } from "../lib/nudges";
import { btnPrimary, btnSecondary } from "./ui";

/**
 * One nudge at a time, under the header (lib/nudges.ts). "Do later" comes
 * back at the next sign-in; "Dismiss forever" does not; the primary action
 * goes where the thing is done and the nudge disappears when it is.
 */
export function NudgeBanner() {
  const { me, preferences, session, updatePreferences } = useAuth();
  const location = useLocation();
  const passkeys = usePasskeyCount();
  const [busy, setBusy] = useState(false);
  const nudge = visibleNudge(me, preferences, session, { passkeyCount: passkeys.data, passkeysPossible: passkeysPossible() }, location.pathname);
  if (!nudge) return null;

  const answer = async (state: NudgeState["state"]) => {
    setBusy(true);
    try {
      await updatePreferences({ nudges: { ...preferences.nudges, [nudge.id]: { state, at: state === "later" ? laterAt(session) : new Date().toISOString() } } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="status" aria-label={nudge.title} className="border-b border-accent/30 bg-accent/10 text-sm text-neutral-900 dark:text-neutral-100">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
        <span>
          <strong>{nudge.title}.</strong> {nudge.body}
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <Link to={nudge.action.to} className={`${btnPrimary} min-h-8 px-3 text-xs`}>
            {nudge.action.label}
          </Link>
          <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => void answer("later")}>
            Do later
          </button>
          <button type="button" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={busy} onClick={() => void answer("never")}>
            Dismiss forever
          </button>
        </span>
      </div>
    </div>
  );
}
